import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Field, InfoTip, UnitInput, grid2 } from '../components/shared/FormKit';
import { SettingsCard, SettingsRow, Toggle, StaffPicker, DailyTimesEditor } from '../components/shared/SettingsKit';
import MailPreview from '../components/shared/MailPreview';
import { fetchJSON } from '../utils/api';
import { firmToday } from '../utils/dateRange';

// Management > Training Follow-up Reminder (/ld/reminders, ld:reminder:edit).
// The daily email that chases the feedback form and the post-course quiz a
// session asks for, once they are N days overdue. One card of settings over
// the Preview & Send card; a single Save for the whole card.

const DAYS_MAX = 3650;
const FIELDS = [
  'enabled', 'feedbackEnabled', 'feedbackDaysAfter', 'quizEnabled', 'quizDaysAfter',
  'repeatEveryDays', 'schedules', 'summaryEnabled', 'summaryRecipientStaffIds', 'sessionsFrom',
];
const NUMBER_FIELDS = ['feedbackDaysAfter', 'quizDaysAfter', 'repeatEveryDays'];

// Whole number within the range the API enforces. The inputs keep whatever
// was typed so an emptied box stays empty (never turns into a silent 0);
// Save refuses until every box is valid.
const dayError = (v) => {
  const s = String(v ?? '').trim();
  if (s === '') return 'Enter a number of days';
  if (!/^\d+$/.test(s)) return 'Whole numbers only (no decimals or minus sign)';
  if (Number(s) > DAYS_MAX) return `At most ${DAYS_MAX} days`;
  return null;
};

// Comparable form of a value, so re-ticking a box or retyping the same number
// does not count as an unsaved change.
const normValue = (k, v) => {
  if (NUMBER_FIELDS.includes(k)) return String(v ?? '').trim();
  if (k === 'schedules') return JSON.stringify((v || []).map(s => [s.hour, s.minute]));
  if (Array.isArray(v)) return JSON.stringify(v.map(String).sort());
  return JSON.stringify(v ?? null);
};
const pick = (obj, keys) => Object.fromEntries(keys.map(k => [k, obj[k]]));

const fieldTip = (body, example) => (
  <InfoTip className="ml-0.5" text={
    <>
      {body && <span className="block mb-1.5">{body}</span>}
      <span className="block"><span className="font-semibold">Example:</span> {example}</span>
    </>
  } />
);

const describeRecipient = (r) => {
  const parts = [];
  if (r.feedbackCount > 0) parts.push(`${r.feedbackCount} feedback form${r.feedbackCount === 1 ? '' : 's'}`);
  if (r.quizCount > 0) parts.push(`${r.quizCount} quiz${r.quizCount === 1 ? '' : 'zes'}`);
  return parts.join(', ') || 'nothing outstanding';
};

const LDReminder = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ld:reminder:edit');
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedMsg, setSavedMsg] = useState(false);
  const [savedTick, setSavedTick] = useState(0);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [s, so] = await Promise.all([
        fetchJSON('/api/ld/reminder-settings'),
        fetchJSON('/api/qm/staff/options?active=true'),
      ]);
      setForm(s.settings);
      setSaved(s.settings);
      setStaff(so.staff || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (canEdit) load(); }, [canEdit, load]);

  if (!canEdit) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-800">Editor access required</h2>
          <p className="text-sm text-red-600 mt-1">You need the Training Follow-up Reminder permission to access this area.</p>
        </div>
      </div>
    );
  }

  if (!form && !loading && error) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-800">Failed to load settings</h2>
          <p className="text-sm text-red-600 mt-1">{error}</p>
          <button onClick={load} className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading || !form) {
    return <div className="p-8 text-center text-slate-500">Loading…</div>;
  }

  const dirty = !!saved && FIELDS.some(k => normValue(k, form[k]) !== normValue(k, saved[k]));
  // A switched-off kind keeps whatever its days box holds; only the switched-on
  // ones must be valid.
  const activeNumberFields = NUMBER_FIELDS.filter(k => (
    (k !== 'feedbackDaysAfter' || form.feedbackEnabled) && (k !== 'quizDaysAfter' || form.quizEnabled)
  ));

  const save = async () => {
    if (activeNumberFields.some(k => dayError(form[k]))) {
      setSaveError('Fix the highlighted number fields before saving.');
      return;
    }
    if (!(form.schedules || []).length) {
      setSaveError('Add at least one send time before saving.');
      return;
    }
    setSaving(true); setSaveError(null); setSavedMsg(false);
    try {
      const payload = pick(form, FIELDS);
      NUMBER_FIELDS.forEach((k) => {
        if (!activeNumberFields.includes(k) && dayError(form[k])) delete payload[k];
        else payload[k] = Number(form[k]);
      });
      const data = await fetchJSON('/api/ld/reminder-settings', { method: 'PUT', body: JSON.stringify(payload) });
      setSaved(data.settings);
      setForm(data.settings);
      setSavedMsg(true);
      setSavedTick(t => t + 1);
      setTimeout(() => setSavedMsg(false), 2500);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveControls = (
    <div className="flex items-center gap-3 shrink-0">
      {savedMsg && <span className="text-sm text-green-600">Saved</span>}
      {dirty && !saving && !savedMsg && <span className="text-xs text-amber-700">Unsaved changes</span>}
      <button onClick={save} disabled={saving || !dirty} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );

  const kindCard = ({ label, switchKey, daysKey, example }) => {
    const on = form[switchKey] === true;
    return (
      <div className={`rounded-lg border border-slate-200 p-3 space-y-3 ${on ? '' : 'bg-slate-50'}`}>
        <Toggle checked={on} onChange={v => setField(switchKey, v)} label={label} />
        <Field
          label={<>Chase from this many days after the session {fieldTip('0 = from the session day itself. Counted from the session date, not from when the session was marked completed.', example)}</>}
          error={on ? dayError(form[daysKey]) : null}
        >
          <UnitInput unit="days" max={DAYS_MAX} disabled={!on} value={form[daysKey]} onChange={v => setField(daysKey, v)} />
        </Field>
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <Link to="/manage" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" />Management</Link>
      <div className="mt-2 mb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Training Follow-up Reminder</h1>
        <p className="text-sm text-slate-500 mt-1">Chase feedback forms and post-course quizzes that are still outstanding after a training session. Saved changes apply to the next scheduled run immediately.</p>
      </div>

      <div className="space-y-4">
        {saveError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
            {saveError}
          </div>
        )}
        <SettingsCard
          title="Follow-up Reminder"
          hint="Who is outstanding is exactly what each person sees under Training follow-ups on their Overview: attended but no feedback; in the quiz audience but not passed the quiz yet (Not passed counts as outstanding). Each person gets one email listing all their outstanding items."
          aside={saveControls}
        >
          <SettingsRow title="Enabled" description="Switch the daily run on or off. Send now on the preview below works either way.">
            <Toggle
              checked={form.enabled}
              onChange={(v) => {
                setField('enabled', v);
                // Switching it on: chase from today by default, so the first run
                // does not mail everyone about every past session.
                if (v && saved && !saved.enabled) setField('sessionsFrom', firmToday());
              }}
              label="Enable follow-up reminder"
            />
          </SettingsRow>
          <SettingsRow
            title="Chase Sessions From"
            description="Only sessions dated on or after this day are chased. It is set to today when the reminder is switched on; leave it blank to chase every past session."
          >
            <div className="max-w-xs">
              <Field label="Session date on or after">
                <input
                  type="date"
                  value={form.sessionsFrom || ''}
                  onChange={(e) => setField('sessionsFrom', e.target.value || null)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </Field>
            </div>
          </SettingsRow>
          <SettingsRow
            title="What to Chase"
            description="Switch each kind on and say how many days after the session date the chasing starts. Staff who have left are never chased."
          >
            <div className={grid2}>
              {kindCard({
                label: 'Feedback form', switchKey: 'feedbackEnabled', daysKey: 'feedbackDaysAfter',
                example: '7 days — session on 1 Apr 2026 → chased from 8 Apr 2026.',
              })}
              {kindCard({
                label: 'Post-course quiz', switchKey: 'quizEnabled', daysKey: 'quizDaysAfter',
                example: '7 days — session on 1 Apr 2026 → chased from 8 Apr 2026.',
              })}
            </div>
          </SettingsRow>
          <SettingsRow
            title="Repeat"
            description="How often the same person is reminded about the same item. A feedback or quiz letter sent by hand from the check-in modal counts as a reminder too."
          >
            <div className="max-w-xs">
              <Field
                label={<>Remind again every {fieldTip('0 = remind once only and never again for that item.', '7 days — reminded on 8 Apr, again on 15 Apr if still outstanding.')}</>}
                error={dayError(form.repeatEveryDays)}
              >
                <UnitInput unit="days" max={DAYS_MAX} value={form.repeatEveryDays} onChange={v => setField('repeatEveryDays', v)} />
              </Field>
            </div>
          </SettingsRow>
          <SettingsRow
            title="Summary"
            description="One extra email per run listing every outstanding item past its threshold, with who was last reminded when, to the staff you pick. Nobody on this list receives it unless chosen here."
          >
            <Toggle checked={form.summaryEnabled} onChange={v => setField('summaryEnabled', v)} label="Also send a summary email" />
            {form.summaryEnabled && (
              <div>
                <p className="text-xs text-slate-500 mb-1.5">Summary Recipients (Search Staff):</p>
                <StaffPicker staff={staff} selectedIds={form.summaryRecipientStaffIds || []} onChange={ids => setField('summaryRecipientStaffIds', ids)} />
              </div>
            )}
          </SettingsRow>
          <SettingsRow title="Send Times" description="Every day at each time listed (Singapore time). Duplicate times are merged on save.">
            <DailyTimesEditor schedules={form.schedules || []} onChange={(list) => setField('schedules', list)} />
          </SettingsRow>
        </SettingsCard>

        <FollowupPreview refreshKey={savedTick} />
      </div>
    </div>
  );
};

// Today's run: who would be mailed, the mail they would get, Send now.
const FollowupPreview = ({ refreshKey }) => {
  const [meta, setMeta] = useState({ kinds: [], outstanding: { feedback: 0, quiz: 0 }, due: { feedback: 0, quiz: 0 } });
  useEffect(() => {
    let cancelled = false;
    fetchJSON('/api/ld/followup-reminder/recipients')
      .then((d) => {
        if (!cancelled) setMeta({ kinds: d.kinds || [], outstanding: d.outstanding || { feedback: 0, quiz: 0 }, due: d.due || { feedback: 0, quiz: 0 } });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [refreshKey]);
  const renderUrl = useCallback((key) => `/api/ld/followup-reminder/render?key=${encodeURIComponent(key)}`, []);
  const kindLabel = (k) => (k === 'feedback' ? 'Feedback forms' : 'Quizzes');
  const covers = meta.kinds.map(k => `${kindLabel(k)} (${meta.due[k]} due of ${meta.outstanding[k]} outstanding)`).join(', ');
  return (
    <MailPreview
      refreshKey={refreshKey}
      recipientsUrl="/api/ld/followup-reminder/recipients"
      renderUrl={renderUrl}
      describe={describeRecipient}
      enabledKey="enabled"
      disabledNote="The follow-up reminder is currently disabled — this is what would be sent today if enabled."
      emptyNote={meta.kinds.length
        ? 'Nobody is due a reminder today with the saved settings.'
        : 'Neither kind is switched on — switch one on above and save.'}
      unmatchedTitle={(n) => `${n} outstanding item${n === 1 ? '' : 's'} belong to people who cannot be emailed`}
      unmatchedNote="These people get no individual reminder. The items still appear in the summary email."
      unmatchedColumns={[
        { key: 'courseTitle', label: 'Session' },
        { key: 'kind', label: 'Item' },
        { key: 'name', label: 'Name' },
        { key: 'reason', label: 'Why' },
      ]}
      send={{
        url: '/api/ld/followup-reminder/send-now',
        body: {},
        covers: covers || 'nothing (neither kind is switched on)',
      }}
      head={meta.kinds.length > 0 && (
        <p className="text-xs text-slate-600 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="font-medium">Today's run covers:</span> {covers}
        </p>
      )}
    />
  );
};

export default LDReminder;
